import { InputManagerSubscriptionTypes } from 'Core/InputManager'
import { ViolationSource } from 'shared/violations/types'
import { Studio } from 'Core/Studio'
import { ITask } from 'Core/TaskAdapter'
import { forEach, isEmpty } from 'lodash'
import { AIDetectorPlugin } from 'Plugins/AIDetector/AIDetector.plugin'
import { PlagiarismPlugin } from 'Plugins/Plagiarism/Plagiarism.plugin'
import { fillDraftData, getFileDownloadUrl, submitTurnDataStreaming } from 'Services/tasks.service'
import type { CSATFeedbackData } from 'Types/CSAT.types'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import {
    AttributeDefinitionType,
    AttributesData,
    ContainerType,
    ConversationalConfigs,
    Conversationalv2TurnDataType,
    Conversationalv2TurnTypes,
    Conversationsv2DataType,
    EditorType,
    ConversationJSONAnnotationData,
    NotificationPropsType,
    StepTypes,
    SubmissionPlayTurnV2APIResponseType,
    TaskAttribute,
    TaskConfigType,
    TaskMode,
    UpdatedAttributesData,
} from 'Types/Tasks.types'
import { createReactElement } from 'shared/utils/react.utils'
import { ActionParam } from 'Core/ActionCore/ActionManager'
import { ITaskBlock } from 'shared/express-interface/blocks-core/ITaskBlock'
import { Conversationv2ContainerStrategy } from 'Core/StateContainerCore/ContainerStrategies/Conversationv2ContainerStrategy'
import { ConversationsTaskv2UI } from 'Tasks/ConversationTaskv2/UI/ConversationTaskv2UI'
import { AttributeGroupEvent } from 'Core/AttributeCore/AttributeGroup/BaseAttributeGroup'
import { getJSONPathFromPath } from 'Utils/attributes.utils'
import { TurnTargetedAttributeGroup } from 'Core/AttributeCore/AttributeGroup/TurnTargetedAttributeGroup'
import { JSONPath } from 'jsonpath-plus'
import { jsonPathToDotNotation } from 'Tasks/JSONAnnotation/utils'
import { JSONObject } from 'Types/Common.types'
import { FileToModelValueType } from 'Types/Attribute.types'
import { isViewerMode } from 'Utils/task.utils'
import { findLastCompleteJSON } from 'Utils/common.utils'
import {
    fillProctoringTimeoutExtraInfo,
    fillProctoringTimeoutValues,
} from 'Utils/proctoringSubmit.utils'

export class ConversationTaskv2
    extends CommonTask<ContainerType.Conversationsv2, Conversationsv2DataType>
    implements ITask<TaskConfigType<ContainerType.Conversationsv2, Conversationsv2DataType>>
{
    private turnData: Conversationalv2TurnDataType[] = []

    private isResponseLoading: boolean = false
    private isResponseStreaming: boolean = false
    private streamingPath: string | null = null
    private abortController: AbortController | null = null
    private activeStreamSignal: AbortSignal | null = null
    private conversationConfigLimit: ConversationalConfigs = {
        turns: 0,
        retryCount: 0,
    }
    private conversationConfigsData: ConversationalConfigs = {
        turns: 0,
        retryCount: 0,
    }
    private turnsCountFromLastResponse: number = 0
    private editIndex: number = -1
    private fileUploadUrls: FileToModelValueType[] = []

    private userMessage: string = ''
    private errorMessage: string | null = null

    constructor(studio: Studio) {
        const conversationContainerStrategyv2 = new Conversationv2ContainerStrategy()
        super(studio, conversationContainerStrategyv2)
        conversationContainerStrategyv2.setContext({
            task: this,
        })
    }

    public initialize(
        config: TaskConfigType<ContainerType.Conversationsv2, Conversationsv2DataType>
    ) {
        this.conversationConfigLimit = {
            turns: config.assignment.task.config.turn_config?.max_allowed_turns ?? -1,
            retryCount:
                config.assignment.task.config.response_generation_config?.max_allowed_times ?? -1,
        }

        const modelRequestConfig = config.assignment.task.config.model_request_config
        if (
            modelRequestConfig?.model_response_mapping_key &&
            modelRequestConfig?.stream_response_mapping_key
        ) {
            // this will store streaming path relative to model response mapping key
            this.streamingPath = modelRequestConfig.stream_response_mapping_key.replace(
                modelRequestConfig.model_response_mapping_key,
                '$'
            )
        }

        const inputEditorBlockConfig = {
            id: 'input-editor-block-1',
            name: 'input-editor-block-1',
            componentProps: {
                onUpdateContent: (message: string) => {
                    this.userMessage = message
                    /*
                     * If message is empty, we need to remove the mandatory violation.
                     * Otherwise, we add the mandatory violation
                     * This is to ensure that the user can submit the task without any mandatory violations
                     * But he won't be able to submit the turn with violations
                     */
                    this.setPluginViolations(!isEmpty(message))
                    this.studio.blockEngine.updateBlock('conversations-input-block-1')
                },
                getValue: () => this.userMessage,
                editorType: EditorType.MarkdownEditor,
                editorConfig: {
                    markdownEditorConfig: {
                        height: '400px',
                    },
                },
            },
        }

        const inputEditorBlock = this.studio.blockEngine.addBlock(
            'input-editor-block',
            inputEditorBlockConfig
        )

        const cardBlock = this.studio.blockEngine.addBlock('card-block', {
            id: 'card-block-1',
            name: 'card-block-1',
            componentProps: {
                children: inputEditorBlock.getElement()?.element,
                title: 'YOUR PROMPT',
            },
        })

        const promptBlock = this.studio.blockEngine.addBlock('prompt-block', {
            id: 'prompt-block-1',
            name: 'prompt-block-1',
        })

        const blocks: ITaskBlock[] = [promptBlock]

        const isEditorStep =
            config.assignment.step?.type === 'MAKER' || config.assignment.step?.type === 'REWORK'

        const isViewerStep = config.mode === TaskMode.Viewer

        const conversationsTurnBlock = this.studio.blockEngine.addBlock(
            'conversations-turn-block',
            {
                id: 'conversations-turn-block-1',
                name: 'conversations-turn-block-1',
                componentProps: {
                    inputEditorBlockElement: cardBlock,
                    turnData: () => this.turnData,
                    userMessage: () => this.userMessage,
                    editIndex: () => this.editIndex,
                    setEditIndex: (index: number) => {
                        this.editIndex = index

                        if (index !== -1) {
                            this.userMessage = this.turnData[index].prompt
                            this.studio.blockEngine
                                .getBlock('input-editor-block-1')
                                ?.setState('answer', this.userMessage)
                        }

                        this.setPluginViolations(!isEmpty(this.userMessage))

                        this.updateUI()
                    },
                    isEditorStep,
                    isResponseStreaming: () => this.isResponseStreaming,
                    streamingPath: () => this.streamingPath,
                    isLoading: () => this.isResponseLoading,
                    turnsCountFromLastResponse: this.turnsCountFromLastResponse,
                    onClickRegenerateContent: this.onClickRegenerateContent,
                    isRetryUnavailable: () => {
                        return (
                            this.isResponseStreaming ||
                            this.conversationConfigsData.retryCount >=
                                this.conversationConfigLimit.retryCount
                        )
                    },
                    mode: config.mode,
                    setFilesList: (index: number, files: FileToModelValueType[]) => {
                        this.turnData[index].media_files = files.map(file => ({
                            download_url: '',
                            path: file.path,
                            file_id: file.file_id,
                        }))
                        this.updateUI()
                    },
                    submitEditTurn: (index: number) => {
                        this.submitTurnData(index)
                    },
                    errors: () => Object.values(this.studio.inputManager.getViolation().error),
                    errorMessage: () => this.errorMessage,
                },
            }
        )

        blocks.push(conversationsTurnBlock)

        if (isEditorStep || isViewerStep) {
            const conversationsInputBlock = this.studio.blockEngine.addBlock(
                'conversations-input-block',
                {
                    id: 'conversations-input-block-1',
                    name: 'conversations-input-block-1',
                    componentProps: {
                        inputEditorBlockElement: cardBlock,
                        editIndex: () => this.editIndex,
                        setEditIndex: (index: number) => {
                            this.editIndex = index
                            this.ui?.updater.forceUpdate()
                        },
                        userMessage: () => this.userMessage,
                        submitTurn: (index: number) => {
                            this.submitTurnData(index)
                        },
                        isTurnExhausted: () =>
                            this.conversationConfigsData.turns ===
                            this.conversationConfigLimit.turns,
                        isLoading: this.isResponseLoading,
                        isResponseStreaming: () => this.isResponseStreaming,
                        setIsLoading: (isLoading: boolean) => {
                            this.isResponseLoading = isLoading
                            this.ui?.updater.forceUpdate()
                        },
                        errors: () => Object.values(this.studio.inputManager.getViolation().error),
                        isEditorPluginsEnabled: () =>
                            this.studio.pluginManager
                                .getInstalledPlugins()
                                .some(plugin =>
                                    ['Soft Rules', 'Readability', 'Sentiment Analysis'].includes(
                                        plugin.id
                                    )
                                ),
                        filesList: () => this.fileUploadUrls,
                        setFilesList: (files: FileToModelValueType[]) => {
                            this.fileUploadUrls = files.map(file => ({
                                download_url: '',
                                path: file.path,
                                file_id: file.file_id,
                            }))
                            this.updateUI()
                        },
                    },
                }
            )

            blocks.push(conversationsInputBlock)
        }

        this.studio.workbench.enqueueUiUpdate(async workbenchUi => {
            await this.initTurnsData(config)

            this.ui = createReactElement(ConversationsTaskv2UI, {
                blocks,
            })

            this.addCommonWorkbenchComponents(workbenchUi, config)
            this.setPluginViolations(false)

            if (
                config.assignment.task.attributes?.filter(item =>
                    [
                        AttributeDefinitionType.EXTRA_INFO,
                        AttributeDefinitionType.QUALITY_EVALUATION,
                    ].includes(item.type)
                ).length > 0
            ) {
                this.attributeManager?.useAttributeGroup({
                    groupKey: AttributeDefinitionType.QUALITY_EVALUATION,
                    type: AttributeDefinitionType.QUALITY_EVALUATION,
                    attributes: config.assignment.task.attributes.filter(
                        item => item.type === AttributeDefinitionType.QUALITY_EVALUATION
                    ),
                    config: {
                        useInCollection: true,
                    },
                })

                this.attributeManager?.useAttributeGroup({
                    groupKey: AttributeDefinitionType.EXTRA_INFO,
                    type: AttributeDefinitionType.EXTRA_INFO,
                    attributes: config.assignment.task.attributes.filter(
                        item => item.type === AttributeDefinitionType.EXTRA_INFO
                    ),
                    config: {
                        useInCollection: true,
                    },
                })

                workbenchUi.rightSidebar.addDivider()
            }
        })

        this.studio.inputManager.subscribeByType('markdown', [
            {
                eventType: InputManagerSubscriptionTypes.Update,
                listener: event => {
                    if (!event) return
                    this.ui?.updater.forceUpdate()
                },
            },
        ])
    }

    addMakerActions(): void {
        const makerActions: ActionParam[] = []
        if (!this.isSavedTask) {
            makerActions.push({
                id: 'Submit',
                config: {
                    onClick: this.onClickSubmitHandler,
                },
            })
        }
        const isSkipTaskConfigEnabled =
            this.initialConfig?.assignment.task.config.skip_task_config?.is_enabled
        if (isSkipTaskConfigEnabled) {
            makerActions.push({
                id: 'Skip',
                config: { title: this.isSavedTask ? 'Skip & Exit' : 'Skip' },
            })
        }
        const step = this.studio.taskConfig.assignment.step
        makerActions.push({
            id: 'SubmitAndExit',
            config: { isCollapsed: !this.isSavedTask, onClick: this.onClickSubmitHandler },
        })
        if (step?.type === StepTypes.Maker || step?.type === StepTypes.Rework || !this.isReadOnly) {
            makerActions.push({
                id: 'Exit',
                config: {
                    onClick: async () => {
                        this.studio.exitTask()
                    },
                },
            })
        } else {
            makerActions.push({ id: 'Exit' })
        }
        makerActions.push({
            id: 'SaveForLater',
            config: { title: this.isSavedTask ? 'Save For Later & Exit' : 'Save For Later' },
        })
        this.actionManager?.addActions(makerActions)
    }

    private async saveTaskForMakerAndRework(
        explicit: boolean,
        options?: {
            onComplete?: (props?: NotificationPropsType) => void
        }
    ) {
        const taskState = this.taskState.getCurrentState()

        const draftOutputContainer = this.taskState.getModifiedDraftOutputContainer(
            this.initialConfig as TaskConfigType<
                ContainerType.Conversationsv2,
                Conversationsv2DataType
            >
        )

        let jsonAttributes: ConversationJSONAnnotationData | undefined = Object.assign(
            {},
            this.taskState?.getDraftOutputContainer()?.data.data.response.main_info.value
                .json_attributes
        )

        if (jsonAttributes) {
            const currentTurn = `$.turn[${this.turnData.length}]`
            const filteredAttributes: typeof jsonAttributes = {}

            Object.keys(jsonAttributes).forEach(path => {
                if (path.startsWith(currentTurn)) {
                    filteredAttributes[path] = jsonAttributes![path]
                }
            })

            jsonAttributes = filteredAttributes
        }

        try {
            await submitTurnDataStreaming({
                userId: taskState?.user_id ?? '',
                submissionId: taskState?.submission.id ?? '',
                timeElapsedInSeconds: Math.floor(
                    this.studio.taskManager.timer.getElapsedTime() / 1000
                ),
                data: {
                    type: Conversationalv2TurnTypes.PromptAutoSave,
                    data: { prompt: this.userMessage },
                },
                extraInfo: this.transformExtraInfo(
                    draftOutputContainer.data.data.response.extra_info
                ),
                supplement: undefined,
                turnAnnotations: {
                    json_attributes: jsonAttributes ?? {},
                },
                promptIndex: undefined,
                pluginData: undefined,
            })

            if (explicit) {
                // We are using function saveTask for key bindings as well as the UI element 'Exit'.
                // In case of key bindings, we don't want the user to be redirected to home page on success.
                // Hence, only notification is shown in case of key bindings.
                if (options?.onComplete) {
                    options?.onComplete?.({
                        type: 'success',
                        message: 'Task saved successfully',
                    })
                } else {
                    this.showNotification('Task saved successfully', 'info')
                }
            }
        } catch (e) {
            if (explicit) {
                this.showNotification((e as Error).message, 'error')
            }
            console.error('Error saving task:', e)
        } finally {
            this.isResponseLoading = false
            this.ui?.updater.forceUpdate()
        }
    }

    async saveTask(
        explicit: boolean,
        options?: {
            onComplete?: (props?: NotificationPropsType) => void
        }
    ) {
        const taskState = this.taskState.getCurrentState()

        const draftOutputContainer = this.taskState.getModifiedDraftOutputContainer(
            this.initialConfig as TaskConfigType<
                ContainerType.Conversationsv2,
                Conversationsv2DataType
            >
        )
        if (!taskState || !draftOutputContainer) {
            console.error('Cannot save task: task state is not initialized')
            if (explicit) {
                this.showNotification('Cannot save task: task state is not initialized', 'error')
            }
            return
        }

        // Skip API call for preview/viewer modes
        if (isViewerMode(this.initialConfig.mode)) {
            return
        }

        if (
            this.initialConfig.mode === TaskMode.Maker ||
            this.initialConfig.mode === TaskMode.Rework
        ) {
            await this.saveTaskForMakerAndRework(explicit, options)
        } else {
            super.saveTask(explicit, options)
        }
    }

    async submitTask(options?: {
        onComplete?: (props?: NotificationPropsType) => void
        feedbackData?: CSATFeedbackData
        useDummyIfEmpty?: boolean
    }) {
        if (options?.useDummyIfEmpty) {
            const stepType = this.initialConfig?.assignment.step?.type
            if (stepType && [StepTypes.Maker, StepTypes.Rework].includes(stepType)) {
                try {
                    const draftOutputContainer = this.taskState?.getDraftOutputContainer()
                    if (!draftOutputContainer) {
                        return super.submitTask(options)
                    }

                    const filled = fillProctoringTimeoutValues(
                        draftOutputContainer,
                        this.initialConfig?.assignment.task.attributes
                    )

                    const jsonAttributes =
                        filled.data?.data?.response?.main_info?.value?.json_attributes ?? {}

                    const currentState = this.taskState?.getCurrentState()
                    const extraInfo = fillProctoringTimeoutExtraInfo(
                        this.initialConfig?.assignment.task.attributes,
                        filled.data?.data?.response?.extra_info
                    )

                    await submitTurnDataStreaming({
                        userId: currentState?.user_id ?? '',
                        submissionId: currentState?.submission.id ?? '',
                        timeElapsedInSeconds: Math.ceil(
                            this.studio.taskManager.timer.getElapsedTime() / 1000
                        ),
                        data: {
                            type: Conversationalv2TurnTypes.ConversationFinalization,
                        },
                        extraInfo: this.transformExtraInfo(extraInfo),
                        supplement: filled.supplement,
                        turnAnnotations: {
                            json_attributes: jsonAttributes,
                        },
                        promptIndex: undefined,
                        pluginData: this.getPluginData(),
                    })

                    options?.onComplete?.({
                        type: 'success',
                        message: 'Time ended. Task was submitted automatically.',
                    })
                    return
                } catch (e) {
                    console.warn(
                        '[proctoring] conversations-v2 duration-end play-turn failed; falling back to draft submit',
                        {
                            stepType,
                            submissionId: this.taskState?.getCurrentState()?.submission.id,
                            error: e,
                        }
                    )
                }
            }
            return super.submitTask(options)
        }

        if (
            this.initialConfig?.assignment.step?.type &&
            [StepTypes.Maker, StepTypes.Rework].includes(this.initialConfig.assignment.step.type)
        ) {
            try {
                const draftOutputContainer = this.taskState?.getDraftOutputContainer()
                if (!draftOutputContainer) {
                    console.error('Cannot submit task: task state is not initialized')
                    return
                }

                let jsonAttributes: ConversationJSONAnnotationData | undefined = Object.assign(
                    {},
                    this.taskState?.getDraftOutputContainer()?.data.data.response.main_info.value
                        .json_attributes
                )

                const currentTurnIndex = this.turnData.length

                if (jsonAttributes) {
                    const currentTurn = `$.turn[${currentTurnIndex}]`
                    const filteredAttributes: typeof jsonAttributes = {}

                    Object.keys(jsonAttributes).forEach(path => {
                        if (path.startsWith(currentTurn)) {
                            filteredAttributes[path] = jsonAttributes![path]
                        }
                    })

                    jsonAttributes = filteredAttributes
                }

                const currentState = this.taskState?.getCurrentState()

                const pluginData = this.getPluginData()
                await submitTurnDataStreaming({
                    userId: currentState?.user_id ?? '',
                    submissionId: currentState?.submission.id ?? '',
                    timeElapsedInSeconds: Math.floor(
                        this.studio.taskManager.timer.getElapsedTime() / 1000
                    ),
                    data: {
                        type: Conversationalv2TurnTypes.ConversationFinalization,
                    },
                    extraInfo: this.transformExtraInfo(
                        draftOutputContainer.data.data.response.extra_info
                    ),
                    supplement: draftOutputContainer.supplement,
                    turnAnnotations: {
                        json_attributes: jsonAttributes ?? {},
                    },
                    promptIndex: undefined,
                    pluginData: pluginData,
                })
                options?.onComplete?.({ type: 'success', message: 'Task submitted successfully' })
            } catch (e) {
                this.studio.workbench.enqueueUiUpdate(ui =>
                    ui.notifier.notification.error({ message: (e as Error).message })
                )
                if ((e as Error).message === 'Task submission cancelled by admin.') {
                    options?.onComplete?.({ type: 'error', message: (e as Error).message })
                }
            }
        } else {
            super.submitTask({
                onComplete: (notifications?: NotificationPropsType) => {
                    this.studio.nextTask(notifications)
                },
                feedbackData: options?.feedbackData,
            })
        }
    }

    private onClickSubmitHandler = async () => {
        if (this.isResponseLoading) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.warning({
                    message: 'Please wait while the response is generating.',
                })
            )
            return
        }

        if (
            this.turnData.length === 0 &&
            (this.initialConfig?.assignment.step?.type === 'MAKER' ||
                this.initialConfig?.assignment.step?.type === 'REWORK')
        ) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.error({
                    message:
                        'Please include at least one prompt in this turn to proceed with task submission.',
                })
            )
            return
        }

        const canSubmit = await this.validateData()
        if (!canSubmit) {
            return
        }

        // Filter out the errors for mandatory plugin violations except custom validation
        const errors = Object.values(this.studio.inputManager.getViolation().error).filter(
            violation =>
                violation.source !== ViolationSource.Plugin ||
                violation.id.includes('custom_validation') ||
                violation.id.includes('live_feedback')
        ).length

        if (errors > 0) {
            this.modalManager?.showModal('violations-modal')
            this.studio.pluginManager.handleSubmissionViolation()
            this.ui?.updater.forceUpdate()
            return
        }

        this.modalManager?.setModalMetaData('submit-modal', {
            taskMode: this.studio.taskConfig.mode,
        })
        this.modalManager?.showModal('submit-modal')
        this.ui?.updater.forceUpdate()
    }

    private addJSONPathAttributes(index?: number) {
        const config = this.initialConfig as TaskConfigType<
            ContainerType.Conversationsv2,
            Conversationsv2DataType
        >

        const attributes = config.assignment.task.attributes.filter(
            attribute => attribute.type === AttributeDefinitionType.JSON_PATH
        )

        const jsonContent = this.taskState
            ?.getDraftOutputContainer()
            ?.data.data.response.main_info.value.turns_data.at(-1)
            ?.external_model_response as JSONObject
        if (!jsonContent) {
            return
        }

        const linkedPathToPossiblePaths: Record<string, string[]> = {}
        const possiblePathToTransformedPath: Record<string, string> = {}
        const pathToAttributes: Record<string, TaskAttribute[]> = {}

        forEach(attributes, attribute => {
            const jsonPath = attribute.linked_to_path
            if (!jsonPath) throw new Error('jsonPath not found for path level attribute')
            let possiblePaths: string[]
            if (linkedPathToPossiblePaths[jsonPath]) {
                possiblePaths = linkedPathToPossiblePaths[jsonPath]
            } else {
                possiblePaths = JSONPath({
                    path: jsonPath,
                    resultType: 'path',
                    json: jsonContent,
                })
                linkedPathToPossiblePaths[jsonPath] = possiblePaths
            }
            forEach(possiblePaths, path => {
                let transformedPath: string
                if (possiblePathToTransformedPath[path]) {
                    transformedPath = possiblePathToTransformedPath[path]
                } else {
                    transformedPath = jsonPathToDotNotation(path, jsonContent)
                    possiblePathToTransformedPath[path] = transformedPath
                }
                if (pathToAttributes[transformedPath]) {
                    pathToAttributes[transformedPath].push(attribute)
                } else {
                    pathToAttributes[transformedPath] = [attribute]
                }
            })
        })

        forEach(pathToAttributes, (attributes, key) => {
            const attributeConfig = {
                type: AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES,
                groupKey: getJSONPathFromPath(key, index ?? this.turnData.length),
                attributes,
                config: {
                    useInCollection: false,
                    props: { header: '' },
                    isDisabled: false,
                },
            }
            this.attributeManager?.useAttributeGroup(attributeConfig)
        })

        const attributeGroups = this.attributeManager?.getAttributeGroupsByDefinitionType(
            AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES
        )

        attributeGroups?.forEach(group => {
            group.on(AttributeGroupEvent.Change, async () => {
                await group.validate()
                this.updateUI()
            })
        })
        this.updateUI()
    }

    private transformExtraInfo(extraInfo: UpdatedAttributesData | undefined): AttributesData {
        if (!extraInfo) {
            return {}
        }

        const transformAttributesData: AttributesData = {}

        for (const [key, data] of Object.entries(extraInfo)) {
            transformAttributesData[key] = {
                data: {
                    value: data.value,
                    value_type: data.value_type,
                },
                value_id: data.value_id ?? crypto.randomUUID(),
            }
        }

        return transformAttributesData
    }

    private async initTurnsData(
        config: TaskConfigType<ContainerType.Conversationsv2, Conversationsv2DataType>
    ) {
        const draftContainer = this.taskState?.getDraftOutputContainer()
        if (!draftContainer) {
            return
        }

        const isEditorStep =
            this.initialConfig?.assignment.step?.type === 'MAKER' ||
            this.initialConfig?.assignment.step?.type === 'REWORK'

        /*
         * If the value is mutable, it means it's the actual draft container and not the output start state container.
         * If it's not mutable, we will not add them to the turn data for Maker and Rework steps as we just want to show them as part of UI.
         * But if it's mutable and it's a Reviewer or QC flow, we will add them to the turn data as we will need to edit and submit them.
         * If value is not mutable, we will loop through them and add them to previousTurnData and attribute groups would have separate keys to just display them.
         */

        const outputStartStateContainer = config.assignment.task.output_start_state_container
        const originalDraftContainer =
            this.studio.taskConfig.assignment.submission?.draft_output_container
        if (
            originalDraftContainer?.data === null &&
            outputStartStateContainer?.data?.data?.response?.main_info?.value &&
            isEditorStep &&
            !isViewerMode(config.mode)
        ) {
            const currentState = this.taskState?.getCurrentState()

            await fillDraftData(
                currentState?.user_id ?? '',
                currentState?.submission.id ?? '',
                outputStartStateContainer.data.data.response.main_info,
                Conversationalv2TurnTypes.DraftFill
            )
        }

        if (draftContainer.data.data.response) {
            const turns = draftContainer.data.data.response.main_info.value.turns_data ?? []

            for (let i = 0; i < turns.length; i++) {
                const turn = turns[i]
                this.turnData.push({
                    prompt: turn.prompt,
                    modelResponse: turn.external_model_response ?? ({} as JSONObject),
                    promptRounds: turn.retry_responses ?? [],
                    media_files: turn.media_files ?? [],
                })

                this.disableAttributeGroupForIndex(i)

                this.addJSONPathAttributes(i + 1)
            }

            const attributeGroups = this.attributeManager?.getAttributeGroupsByDefinitionType(
                AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES
            )

            attributeGroups?.forEach(group => {
                group.on(AttributeGroupEvent.Change, () => {
                    this.studio.blockEngine.updateBlock('conversations-input-block-1')
                    this.studio.blockEngine.updateBlock('conversations-turn-block-1')
                    this.ui?.updater.forceUpdate()
                })
            })

            //TODO - Think of a better way to do this
            setTimeout(() => {
                this.attributeManager?.validateGroupsByDefinitionType(
                    AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES
                )
            }, 500)

            this.userMessage =
                draftContainer.data.data.response.main_info.value.partially_saved_prompt ?? ''
        }

        this.conversationConfigsData.retryCount =
            (this.turnData.at(-1)?.promptRounds?.length ?? 0) - 1
        this.conversationConfigsData.turns = this.turnData.length

        this.updateUI()
    }

    /*
    We need to disable all plugin violations when the task is initialized. 
    We only need to check for plugin violations before the user submits the prompt for next turn.
    */
    private setPluginViolations(enable: boolean) {
        const plugins = this.studio.pluginManager.getInstalledPlugins()
        plugins.forEach(plugin => {
            if (['AI Detection', 'Plagiarism'].includes(plugin.id)) {
                if (enable) {
                    ;(plugin as PlagiarismPlugin | AIDetectorPlugin).addMandatoryViolation()
                } else {
                    ;(plugin as PlagiarismPlugin | AIDetectorPlugin).removeMandatoryViolation()
                }
            }
        })

        this.ui?.updater.forceUpdate()
    }

    private onClickRegenerateContent = async () => {
        this.cleanupStreamingOperations()
        this.abortController = new AbortController()

        const lastTurnData = this.turnData.at(-1)
        lastTurnData!.modelResponse = {}

        this.errorMessage = null
        this.isResponseLoading = true
        this.updateUI()

        const currentState = this.taskState?.getCurrentState()
        const draftOutputContainer = this.taskState?.getDraftOutputContainer()
        if (!draftOutputContainer) {
            console.error('Cannot submit turn data: task state is not initialized')
            return
        }

        const attributeGroups = this.attributeManager?.getAttributeGroupsByDefinitionType(
            AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES
        )

        const groupKey = `$.turn[${this.turnData.length - 1}].`
        const group = attributeGroups?.find(group => group.groupKey?.includes(groupKey))
        if (group) {
            ;(group as TurnTargetedAttributeGroup).removeAllAttributes()
        }

        try {
            const reader = await submitTurnDataStreaming({
                userId: currentState?.user_id ?? '',
                submissionId: currentState?.submission.id ?? '',
                timeElapsedInSeconds: Math.floor(
                    this.studio.taskManager.timer.getElapsedTime() / 1000
                ),
                data: {
                    type: Conversationalv2TurnTypes.PromptRegeneration,
                    data: { prompt: lastTurnData?.prompt },
                },
                extraInfo: this.transformExtraInfo(
                    draftOutputContainer.data.data.response.extra_info
                ),
            })

            if (!reader) {
                throw new Error('Failed to get response')
            }

            let isFirstChunkProcessed = false
            let areJSONAttributesAdded = false
            await this.streamResponseAndUpdateTurnData(
                reader,
                partialResponse => {
                    if (!isFirstChunkProcessed && partialResponse) {
                        this.addJSONPathAttributes(this.turnData.length)
                        isFirstChunkProcessed = true
                        areJSONAttributesAdded = true
                    }
                },
                (completeResponse: SubmissionPlayTurnV2APIResponseType) => {
                    const lastTurnData =
                        completeResponse.submission.output_container.data.data.response.main_info.value.turns_data.at(
                            -1
                        )
                    draftOutputContainer.data.data.response.main_info.value =
                        completeResponse.submission.output_container.data.data.response.main_info.value
                    this.turnData.at(-1)!.modelResponse =
                        lastTurnData?.external_model_response ?? {}
                    this.conversationConfigsData.retryCount =
                        (lastTurnData?.retry_responses?.length ?? 0) - 1
                    if (!areJSONAttributesAdded) {
                        this.addJSONPathAttributes(this.turnData.length)
                        areJSONAttributesAdded = true
                    }
                },
                this.abortController.signal
            )
        } catch (e) {
            if (!this.abortController?.signal.aborted) {
                this.turnData.at(-1)!.error = (e as Error).message
            }
        } finally {
            this.isResponseLoading = false
            this.updateUI()
        }
    }

    private clearAttributeGroupsFromIndex = (index: number) => {
        const attributeGroups = this.attributeManager?.getAttributeGroupsByDefinitionType(
            AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES
        )

        for (let i = index; i <= this.turnData.length; i++) {
            const groupKey = `$.turn[${i}].`
            const group = attributeGroups?.find(group => group.groupKey?.includes(groupKey))
            if (group) {
                ;(group as TurnTargetedAttributeGroup).removeAllAttributes()
            }
        }

        this.updateUI()
    }

    private enableAttributeGroupForIndex = (index: number) => {
        const attributeGroups = this.attributeManager?.getAttributeGroupsByDefinitionType(
            AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES
        )

        attributeGroups?.forEach(group => {
            const turnIndexInGroupKey = group.groupKey?.match(/^\$\.turn\[\d+\]/)![0]
            if (turnIndexInGroupKey) {
                const turnIndex = turnIndexInGroupKey.replace('$.turn[', '').replace(']', '')
                if (index === parseInt(turnIndex)) {
                    ;(group as TurnTargetedAttributeGroup).enable()
                }
            }
        })

        this.updateUI()
    }

    private disableAttributeGroupForIndex = (index: number) => {
        const attributeGroups = this.attributeManager?.getAttributeGroupsByDefinitionType(
            AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES
        )

        attributeGroups?.forEach(group => {
            const turnIndexInGroupKey = group.groupKey?.match(/^\$\.turn\[\d+\]/)?.[0]
            if (turnIndexInGroupKey) {
                const turnIndex = turnIndexInGroupKey.replace('$.turn[', '').replace(']', '')
                if (index === parseInt(turnIndex)) {
                    ;(group as TurnTargetedAttributeGroup).disable()
                }
            }
        })

        this.updateUI()
    }

    /**
     * Processes a parsed streaming response (partial or complete)
     */
    private processStreamingResponse(
        partial: SubmissionPlayTurnV2APIResponseType | { message: JSONObject },
        isFirstChunk: boolean,
        onPartial?: (partialResponse: { message: JSONObject }) => void,
        onComplete?: (completeResponse: SubmissionPlayTurnV2APIResponseType) => void
    ): { isFirstChunk: boolean; shouldContinue: boolean } {
        let currentIsFirstChunk = isFirstChunk

        if (currentIsFirstChunk) {
            this.isResponseLoading = false
            this.isResponseStreaming = true
            currentIsFirstChunk = false
        }

        const isMessageOnly = 'message' in partial && !('submission' in partial)
        const isFullSubmission = 'submission' in partial

        if (isMessageOnly) {
            const messagePartial = partial as { message: JSONObject }
            const lastTurnIndex = this.turnData.length - 1
            if (lastTurnIndex >= 0) {
                this.turnData[lastTurnIndex].modelResponse =
                    messagePartial.message ?? ({} as JSONObject)
                this.updateDraftContainerTurnData(
                    this.turnData.map(turn => ({
                        prompt: turn.prompt,
                        external_model_response: turn.modelResponse,
                        retry_responses: turn.promptRounds,
                        media_files: turn.media_files,
                    }))
                )
                onPartial?.(messagePartial)
            }
            this.updateUI()
            return { isFirstChunk: currentIsFirstChunk, shouldContinue: false }
        } else if (isFullSubmission) {
            this.updateDraftContainerTurnData(
                (partial as SubmissionPlayTurnV2APIResponseType).submission.output_container.data
                    .data.response.main_info.value.turns_data
            )
            this.isResponseStreaming = false
            onComplete?.(partial as SubmissionPlayTurnV2APIResponseType)
            this.updateUI()
            return { isFirstChunk: currentIsFirstChunk, shouldContinue: false }
        }

        return { isFirstChunk: currentIsFirstChunk, shouldContinue: true }
    }

    /**
     * Processes the final buffer when the stream is complete
     */
    private processFinalBuffer(
        buffer: string,
        onComplete?: (completeResponse: SubmissionPlayTurnV2APIResponseType) => void
    ): void {
        if (buffer) {
            try {
                const finalResponse = JSON.parse(buffer) as SubmissionPlayTurnV2APIResponseType
                this.isResponseStreaming = false
                onComplete?.(finalResponse)
            } catch {
                console.error('Failed to parse final JSON')
                throw new Error('Invalid model response format.')
            }
        } else {
            this.isResponseLoading = false
            this.isResponseStreaming = false
        }
    }

    /**
     * Processes chunks from the streaming buffer
     */
    private processStreamingChunks(
        buffer: string,
        isFirstChunk: boolean,
        onPartial?: (partialResponse: { message: JSONObject }) => void,
        onComplete?: (completeResponse: SubmissionPlayTurnV2APIResponseType) => void
    ): { processedBuffer: string; isFirstChunk: boolean } {
        let processedBuffer = buffer
        let currentIsFirstChunk = isFirstChunk

        while (processedBuffer.trim()) {
            const jsonResult = findLastCompleteJSON(processedBuffer)

            if (!jsonResult) {
                break
            }

            try {
                const partial = JSON.parse(jsonResult.jsonStr) as
                    | SubmissionPlayTurnV2APIResponseType
                    | { message: JSONObject }

                const result = this.processStreamingResponse(
                    partial,
                    currentIsFirstChunk,
                    onPartial,
                    onComplete
                )

                currentIsFirstChunk = result.isFirstChunk
                processedBuffer = jsonResult.remainingBuffer

                if (!result.shouldContinue) {
                    break
                }
            } catch (e) {
                console.error('Failed to parse JSON:', jsonResult.jsonStr, e)
                break
            }
        }

        return { processedBuffer, isFirstChunk: currentIsFirstChunk }
    }

    private streamResponseAndUpdateTurnData = async (
        reader: ReadableStreamDefaultReader<Uint8Array> | null,
        onPartial?: (partialResponse: { message: JSONObject }) => void,
        onComplete?: (completeResponse: SubmissionPlayTurnV2APIResponseType) => void,
        signal?: AbortSignal
    ) => {
        if (!reader) {
            throw new Error('Something went wrong!')
        }

        const decoder = new TextDecoder()
        let done = false
        let buffer = ''
        let isFirstChunk = true

        const abortHandler = () => {
            reader.cancel().catch(() => {})
        }

        this.activeStreamSignal = signal ?? null
        signal?.addEventListener('abort', abortHandler)

        try {
            while (!done) {
                if (signal?.aborted) break

                const { value, done: streamDone } = await reader.read()
                done = streamDone

                const chunk = decoder.decode(value || new Uint8Array(), { stream: true })
                buffer += chunk

                if (!done) {
                    const result = this.processStreamingChunks(
                        buffer,
                        isFirstChunk,
                        onPartial,
                        onComplete
                    )
                    buffer = result.processedBuffer
                    isFirstChunk = result.isFirstChunk
                } else {
                    this.processFinalBuffer(buffer, onComplete)
                }
            }
        } catch (err) {
            if (signal?.aborted) {
                return
            }
            throw err
        } finally {
            signal?.removeEventListener('abort', abortHandler)
            if (this.activeStreamSignal === signal) {
                this.activeStreamSignal = null
            }
            this.isResponseStreaming = false
        }
    }

    private submitTurnData = async (promptIndex: number) => {
        if (this.isResponseStreaming) {
            this.errorMessage = 'Please wait while the response is being generated'
            this.updateUI()
            return
        }

        const isValidData = await this.attributeManager?.validateGroupsByDefinitionType(
            AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES
        )

        if (!isValidData && promptIndex === -1) {
            this.errorMessage = 'Please complete all the attributes before submitting.'
            this.updateUI()
            return
        }

        this.errorMessage = null
        this.isResponseLoading = true
        this.updateUI()

        this.conversationConfigsData.retryCount = 0

        const currentState = this.taskState?.getCurrentState()

        let type: string
        let jsonAttributes: ConversationJSONAnnotationData | undefined = Object.assign(
            {},
            this.taskState?.getDraftOutputContainer()?.data.data.response.main_info.value
                .json_attributes
        )

        if (promptIndex !== -1) {
            type = Conversationalv2TurnTypes.PromptEdit
            jsonAttributes = undefined
        } else if (this.turnData.length === this.turnsCountFromLastResponse) {
            type = Conversationalv2TurnTypes.Conversation
        } else {
            type = Conversationalv2TurnTypes.ConversationNewTurn
        }

        // Filter jsonAttributes for the current turn only (excluding new conversation case)
        if (jsonAttributes) {
            const currentTurn = `$.turn[${
                promptIndex !== -1 ? promptIndex + 1 : this.turnData.length
            }]`
            const filteredAttributes: typeof jsonAttributes = {}

            Object.keys(jsonAttributes).forEach(path => {
                if (path.startsWith(currentTurn)) {
                    filteredAttributes[path] = jsonAttributes![path]
                }
            })

            jsonAttributes = filteredAttributes
        }

        let mediaFiles: FileToModelValueType[] = []

        if (promptIndex !== -1) {
            this.turnData[promptIndex] = {
                prompt: this.userMessage,
                modelResponse: {},
                promptRounds: [],
                media_files: this.turnData[promptIndex].media_files,
            }

            this.clearAttributeGroupsFromIndex(promptIndex + 1)
            this.turnData = this.turnData.slice(0, promptIndex + 1)

            this.enableAttributeGroupForIndex(this.turnData.length)
        } else {
            mediaFiles = await Promise.all(
                this.fileUploadUrls.map(async file => {
                    const downloadUrl = await getFileDownloadUrl(file.path)
                    return {
                        download_url: downloadUrl,
                        path: file.path,
                        file_id: file.file_id,
                    }
                })
            )

            this.turnData.push({
                prompt: this.userMessage,
                modelResponse: {},
                promptRounds: [],
                media_files: mediaFiles,
            })
            this.disableAttributeGroupForIndex(this.turnData.length - 1)
        }

        this.updateUI()

        const payload = {
            type,
            data: { prompt: this.userMessage, media_files: [...mediaFiles] },
        }

        this.userMessage = ''
        this.fileUploadUrls = []
        this.editIndex = -1
        this.isResponseLoading = true
        this.updateUI()
        this.studio.blockEngine.getBlock('input-editor-block-1')?.setState('answer', '')

        this.studio.pluginManager.getInstalledPlugins().forEach(plugin => {
            plugin.reset()
        })

        this.cleanupStreamingOperations()
        this.abortController = new AbortController()

        try {
            const reader = await submitTurnDataStreaming({
                userId: currentState?.user_id ?? '',
                submissionId: currentState?.submission.id ?? '',
                timeElapsedInSeconds: Math.floor(
                    this.studio.taskManager.timer.getElapsedTime() / 1000
                ),
                data: payload,
                extraInfo: undefined,
                supplement: undefined,
                turnAnnotations: {
                    json_attributes: jsonAttributes ?? {},
                },
                promptIndex: promptIndex !== -1 ? promptIndex : undefined,
            })

            if (!reader) {
                throw new Error('Failed to get response')
            }

            let isFirstChunkProcessed = false
            let areJSONAttributesAdded = false
            await this.streamResponseAndUpdateTurnData(
                reader,
                partialResponse => {
                    if (!isFirstChunkProcessed && partialResponse) {
                        this.addJSONPathAttributes()
                        isFirstChunkProcessed = true
                        areJSONAttributesAdded = true
                    }
                },
                (completeResponse: SubmissionPlayTurnV2APIResponseType) => {
                    const turnData =
                        completeResponse.submission.output_container.data.data.response.main_info
                            .value.turns_data
                    const prevDraftContainer = this.taskState?.getDraftOutputContainer()
                    if (prevDraftContainer) {
                        prevDraftContainer.data.data.response.main_info.value.json_attributes =
                            completeResponse.submission.output_container.data.data.response.main_info.value.json_attributes
                    }
                    this.updateDraftContainerTurnData(turnData)
                    this.turnData.at(-1)!.modelResponse =
                        completeResponse.submission.output_container.data.data.response.main_info.value.turns_data.at(
                            -1
                        )?.external_model_response ?? {}
                    this.conversationConfigsData.turns = this.turnData.length
                    this.conversationConfigsData.retryCount =
                        (turnData.at(-1)?.retry_responses?.length ?? 0) - 1
                    if (!areJSONAttributesAdded) {
                        this.addJSONPathAttributes()
                        areJSONAttributesAdded = true
                    }
                },
                this.abortController.signal
            )
        } catch (e) {
            if (!this.abortController?.signal.aborted) {
                this.turnData.at(-1)!.error = (e as Error).message
            }
        } finally {
            this.editIndex = -1
            this.isResponseLoading = false
            this.updateUI()
        }
    }

    private updateDraftContainerTurnData = (
        turnData: Conversationsv2DataType['response']['main_info']['value']['turns_data']
    ) => {
        const prevDraftContainer = this.taskState?.getDraftOutputContainer()

        if (prevDraftContainer?.data?.data?.response && turnData) {
            prevDraftContainer.data.data.response.main_info.value.turns_data = turnData.map(
                turn => ({
                    prompt: turn.prompt,
                    external_model_response: turn.external_model_response,
                    retry_responses: turn.retry_responses ?? [],
                    media_files: turn.media_files ?? [],
                })
            )
            this.taskState?.updateDraftOutputContainer(prevDraftContainer)
        }
    }

    private updateUI = () => {
        this.studio.blockEngine.updateBlock('conversations-turn-block-1')
        this.studio.blockEngine.updateBlock('conversations-input-block-1')
        this.ui?.updater.forceUpdate()
    }

    private cleanupStreamingOperations = () => {
        this.abortController?.abort()
        this.activeStreamSignal = null
        this.isResponseStreaming = false
    }

    public destroy = () => {
        this.cleanupStreamingOperations()
    }
}
